import { createFileRoute } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Check, Wallet, X } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { AdminPageHeader, StatCard } from "@/components/admin/admin-shell";
import { CrudManager } from "@/components/admin/crud-manager";
import { adminList, adminUpdate } from "@/lib/admin";
import { azError } from "@/lib/az-errors";
import { formatPrice } from "@/lib/data";

export const Route = createFileRoute("/_authenticated/admin/odenisler")({
  component: Page,
});

const kindLabels: Record<string, string> = {
  topup: "Balans artımı",
  withdraw: "Çıxarış",
  vip: "VIP ödənişi",
  premium: "Premium ödənişi",
  store: "Mağaza abunəsi",
};

const statusLabels: Record<string, string> = {
  pending: "Gözləyir",
  completed: "Təsdiqlənib",
  failed: "Uğursuz",
};

function Page() {
  const qc = useQueryClient();
  const tx = useQuery({ queryKey: ["admin", "transactions"], queryFn: () => adminList("transactions") });

  const setStatus = useMutation({
    mutationFn: ({ id, status }: { id: string; status: string }) =>
      adminUpdate("transactions", id, { status }),
    onSuccess: () => {
      toast.success("Ödəniş statusu yeniləndi");
      qc.invalidateQueries({ queryKey: ["admin", "transactions"] });
    },
    onError: (e) => toast.error(azError(e)),
  });

  const rows = tx.data ?? [];
  const income = rows
    .filter((r) => r["status"] === "completed" && Number(r["amount"]) > 0)
    .reduce((s, r) => s + Number(r["amount"] ?? 0), 0);
  const pending = rows.filter((r) => r["status"] === "pending").length;

  return (
    <div>
      <AdminPageHeader
        title="Ödənişlər və balans"
        description="VIP/Premium ödənişləri, balans artımları və əməliyyat tarixçəsi"
      />
      <div className="mb-6 grid gap-4 sm:grid-cols-3">
        <StatCard label="Ümumi gəlir" value={formatPrice(income)} icon={Wallet} />
        <StatCard label="Gözləyən ödənişlər" value={String(pending)} icon={Wallet} />
        <StatCard label="Əməliyyat sayı" value={String(rows.length)} icon={Wallet} />
      </div>

      <CrudManager
        table="transactions"
        addLabel="Yeni əməliyyat"
        emptyText="Hələ ödəniş yoxdur."
        fields={[
          { name: "user_id", label: "İstifadəçi ID" },
          { name: "amount", label: "Məbləğ (AZN)", type: "number" },
          {
            name: "kind",
            label: "Növ",
            type: "select",
            options: Object.entries(kindLabels).map(([value, label]) => ({ value, label })),
          },
          {
            name: "status",
            label: "Status",
            type: "select",
            options: Object.entries(statusLabels).map(([value, label]) => ({ value, label })),
          },
          { name: "method", label: "Ödəniş üsulu", placeholder: "kart, admin, balans…" },
          { name: "note", label: "Qeyd", type: "textarea" },
        ]}
        columns={[
          { name: "created_at", label: "Tarix", render: (r) => new Date(r["created_at"]).toLocaleDateString("az-AZ") },
          { name: "amount", label: "Məbləğ", render: (r) => formatPrice(Number(r["amount"] ?? 0)) },
          { name: "kind", label: "Növ", render: (r) => kindLabels[r["kind"]] ?? r["kind"] },
          { name: "method", label: "Üsul" },
          {
            name: "status",
            label: "Status",
            render: (r) => (
              <Badge
                className="rounded-full"
                variant={r["status"] === "completed" ? "secondary" : r["status"] === "failed" ? "destructive" : "outline"}
              >
                {statusLabels[r["status"]] ?? r["status"]}
              </Badge>
            ),
          },
        ]}
        extraActions={(r) => (
          <>
            {r["status"] !== "completed" && (
              <Button
                size="sm"
                variant="ghost"
                className="rounded-full"
                onClick={() => setStatus.mutate({ id: String(r["id"]), status: "completed" })}
              >
                <Check className="size-4" />
              </Button>
            )}
            {r["status"] !== "failed" && (
              <Button
                size="sm"
                variant="ghost"
                className="rounded-full"
                onClick={() => setStatus.mutate({ id: String(r["id"]), status: "failed" })}
              >
                <X className="size-4" />
              </Button>
            )}
          </>
        )}
      />
    </div>
  );
}
